import React, { FC, ReactNode, useEffect } from "react";
import { Spinner } from "@cloudscape-design/components";
import { useAuthStore } from "../../../store/auth-store";
import { useNotificationStore } from "../../../store/notification-store";
import AccessDeniedPage from "./AccessDeniedPage";

interface ProtectedRouteProps {
  /** The workflow type to check access for (e.g., 'MFR', 'PPM', 'RVM') */
  workflowType: string;
  /** The child components to render if access is granted */
  children: ReactNode;
}

/**
 * ProtectedRoute component that wraps workflow routes and enforces authorization.
 * 
 * This component:
 * 1. Shows a loading spinner while authorization is being determined
 * 2. Shows an error alert if initialization failed
 * 3. Shows an access denied page if the user doesn't have access
 * 4. Renders the children if the user has access
 * 
 * Authorization is initialized in App.tsx on app startup.
 * Authorization follows the "fail closed" principle - if authorization
 * cannot be determined, access is denied.
 */
const ProtectedRoute: FC<ProtectedRouteProps> = ({ workflowType, children }) => {
  const { 
    isInitialized, 
    isInitializing, 
    hasAccess,
    error
  } = useAuthStore();
  const { addNotification } = useNotificationStore();

  // Show error notification if initialization failed
  useEffect(() => {
    if (error) {
      addNotification(
        'error',
        'There was a problem loading your permissions. Please refresh the page or contact support if the issue persists.',
        'Unable to load authorization information',
        { dismissible: true }
      );
    }
  }, [error, addNotification]);

  // Show loading spinner while initializing
  if (isInitializing || !isInitialized) {
    return (
      <div 
        style={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center", 
          height: "100vh" 
        }}
      >
        <Spinner size="large" />
      </div>
    );
  }

  // If error, still show access denied (fail closed)
  if (error || !hasAccess(workflowType)) {
    return <AccessDeniedPage workflowType={workflowType} />;
  }

  // User has access - render children
  return <>{children}</>;
};

export default ProtectedRoute;
